// NAS カーソル取り込み（v1.4.0）— 明細サイドパネル。
// イベントは必ず有界・ページ単位で取得する（DOM を無制限に増やさない）。
// 相対パスと分類だけを表示し、本文・カーソル・トークン・絶対パス・内部URL・
// 環境変数は扱わない。
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { CircleNotch, Warning, ArrowsClockwise } from '@phosphor-icons/react';
import { deriveIngestState, shortId } from './nasJobState';
import { NasIngestRootCard } from './NasIngestRootCard';

const EVENT_PAGE = 50;
const MAX_EVENT_ROWS = 200;
const MAX_ROOT_CARDS = 50;

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const tok = localStorage.getItem('token');
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const FILTERS = [
  ['', 'すべて', 'All'],
  ['skipped', '対象外のみ', 'Skipped only'],
  ['failed', 'エラーのみ', 'Failed only'],
];

const n = (v) => Number(v || 0).toLocaleString('ja-JP');
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString('ja-JP') : '—');

export function NasIngestDetailSheet({ open, onOpenChange, jobId, returnFocusRef }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState('');
  const [more, setMore] = useState(false);

  const load = useCallback(async () => {
    if (!open || !jobId) return;
    setLoading(true); setError(null);
    try {
      const [j, e] = await Promise.all([
        axios.get(`${API}/api/crawler/nas/ingest/${jobId}`, { headers: authHeader() }),
        axios.get(`${API}/api/crawler/nas/runs/${jobId}/events`, {
          params: { limit: EVENT_PAGE, skip: 0, ...(filter ? { status: filter } : {}) },
          headers: authHeader() }),
      ]);
      setJob(j.data); setEvents(e.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError({
        status: err?.response?.status || 0,
        category: (d && typeof d === 'object' ? d.category : null) || 'request_failed',
        message: (d && typeof d === 'object' ? d.message : d) || '明細を取得できませんでした',
      });
      setJob(null); setEvents(null);
    } finally { setLoading(false); }
  }, [open, jobId, filter]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    const rows = events?.events || [];
    if (!events?.has_more || rows.length >= MAX_EVENT_ROWS) return;
    setMore(true);
    try {
      const r = await axios.get(`${API}/api/crawler/nas/runs/${jobId}/events`, {
        params: { limit: EVENT_PAGE, skip: rows.length,
          ...(filter ? { status: filter } : {}) },
        headers: authHeader() });
      setEvents((prev) => ({
        ...r.data,
        events: [...(prev?.events || []), ...(r.data?.events || [])].slice(0, MAX_EVENT_ROWS),
      }));
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError({ status: err?.response?.status || 0, category: 'events_page_failed',
        message: (d && typeof d === 'object' ? d.message : d) || 'イベントの追加取得に失敗しました' });
    } finally { setMore(false); }
  };

  const st = deriveIngestState(job);
  const rows = events?.events || [];
  const atCap = rows.length >= MAX_EVENT_ROWS;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right"
        className="w-full sm:max-w-xl overflow-y-auto bg-white"
        onCloseAutoFocus={(e) => { e.preventDefault(); returnFocusRef?.current?.focus(); }}
        data-testid="nas-ingest-detail-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="text-base" data-testid="nas-ingest-detail-title">
            カーソル取り込みの明細
            <span className="block text-[11px] font-normal text-zinc-500">
              Cursor ingestion details
            </span>
          </SheetTitle>
          <SheetDescription className="text-[11.5px]">
            実行ID <span className="font-mono" data-testid="nas-ingest-detail-job-id">{shortId(jobId)}</span>
            {' · '}{st.labelJa} / {st.labelEn}
          </SheetDescription>
        </SheetHeader>

        {loading && !job && (
          <div className="py-10 flex items-center gap-2 text-[13px] text-zinc-500"
            data-testid="nas-ingest-detail-loading">
            <CircleNotch size={16} className="animate-spin" /> 読み込み中… / Loading…
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12px] text-rose-900"
            data-testid="nas-ingest-detail-error">
            <p className="font-semibold flex items-center gap-1.5">
              <Warning size={14} weight="fill" /> {error.message}
            </p>
            <p className="mt-0.5 text-[11px] opacity-80">
              分類 / type: <span className="font-mono">{error.category}</span>
            </p>
            <Button size="sm" variant="outline" onClick={load}
              className="mt-2 h-7 text-[11px]" data-testid="nas-ingest-detail-retry">
              <ArrowsClockwise size={12} className="mr-1" /> 再試行 / Retry
            </Button>
          </div>
        )}

        {job && (
          <div className="mt-4 space-y-4">
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11.5px]">
              {[
                ['状態', 'Status', `${st.labelJa} / ${st.labelEn}`],
                ['エージェント', 'Agent', `v${job.agent_version || '—'} (${job.agent_contract || '—'})`],
                ['ビルド', 'Build', job.agent_build_revision || '—'],
                ['1ページ件数', 'Page size', n(job.page_size)],
                ['ルート', 'Roots', `${n(job.roots_complete)} / ${n(job.total_roots)}`],
                ['巡回回数', 'Rounds', n(job.rounds)],
                ['NASページ取得', 'Pages fetched', n(job.nas_page_requests)],
                ['再開回数', 'Resumes', n(job.resume_count)],
                ['開始', 'Started', fmtDT(job.started_at)],
                ['終了', 'Finished', fmtDT(job.finished_at)],
                ['最終応答', 'Last heartbeat', fmtDT(job.heartbeat_at)],
                ['DB 書込 / 読取', 'DB writes / reads', `${n(job.db_writes)} / ${n(job.db_queries)}`],
              ].map(([ja, en, val]) => (
                <div key={ja} className="min-w-0">
                  <dt className="text-zinc-500">{ja}<span className="ml-1 text-[9.5px] text-zinc-400">{en}</span></dt>
                  <dd className="font-semibold text-zinc-800 break-words">{val}</dd>
                </div>
              ))}
            </dl>

            {job.stale && (
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11.5px]"
                data-testid="nas-ingest-detail-stale">
                <p className="font-semibold text-zinc-700">
                  削除照合 / Stale-record reconciliation
                </p>
                <p className="mt-0.5 text-zinc-600">
                  候補 <b className="tabular-nums">{n(job.stale.candidates)}</b> 件
                  {' · '}削除 <b className={`tabular-nums ${Number(job.stale.deleted) > 0 ? 'text-rose-700' : ''}`}>{n(job.stale.deleted)}</b> 件
                  {' · '}実行 {job.stale.sweep_ran ? 'あり / ran' : 'なし / did not run'}
                </p>
                <p className="mt-0.5 text-[10.5px] text-zinc-500">
                  理由 / reason: <span className="font-mono">{job.stale.reason || '—'}</span>
                  {Number(job.stale.skipped_due_to_failures) > 0 && (
                    <> · エラーがあったため除外したルート {n(job.stale.skipped_due_to_failures)} 件</>
                  )}
                </p>
              </div>
            )}

            <div>
              <p className="text-[11px] font-semibold text-zinc-500 mb-1.5">
                ルートごとの進捗 / Progress by root
                {job.roots_truncated_for_display && (
                  <span className="ml-1 text-[10px] font-normal text-amber-700">
                    （先頭 {MAX_ROOT_CARDS} 件のみ表示）
                  </span>
                )}
              </p>
              <div className="space-y-1.5">
                {(job.roots || []).slice(0, MAX_ROOT_CARDS).map((r) => (
                  <NasIngestRootCard key={r.path} root={r}
                    isCurrent={r.path === job.current_root}
                    testIdPrefix="nas-ingest-detail-root" />
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <p className="text-[11px] font-semibold text-zinc-500">
                  ファイル明細 / File events
                </p>
                <div className="flex-1" />
                <div className="flex gap-1">
                  {FILTERS.map(([v, ja, en]) => (
                    <button key={v || 'all'} type="button" title={en}
                      onClick={() => setFilter(v)}
                      className={`px-2 py-0.5 rounded border text-[10.5px] ${
                        filter === v ? 'border-teal-300 bg-teal-50 text-teal-800 font-semibold'
                          : 'border-zinc-200 bg-white text-zinc-600'}`}
                      data-testid={`nas-ingest-detail-filter-${v || 'all'}`}>
                      {ja}
                    </button>
                  ))}
                </div>
              </div>

              {events && events.retained === false ? (
                <p className="text-[11.5px] text-zinc-500 rounded border border-dashed border-zinc-200 px-3 py-2.5"
                  data-testid="nas-ingest-detail-events-none">
                  この実行の明細は保存されていません（「対象外・エラーが 0 件」という意味では
                  ありません）。
                  <span className="block text-[10.5px] text-zinc-400">
                    No per-file events were retained for this run.
                  </span>
                </p>
              ) : (
                <>
                  <p className="text-[10.5px] text-zinc-500 mb-1" data-testid="nas-ingest-detail-events-count">
                    表示 {n(rows.length)} / 該当 {n(events?.matching)} 件
                    （記録上限 {n(job.event_cap)} 件
                    {job.events_capped && '・上限に達したため以降は記録していません'}）
                  </p>
                  <ul className="divide-y divide-zinc-100 rounded border border-zinc-200 overflow-hidden"
                    data-testid="nas-ingest-detail-events">
                    {rows.map((ev, i) => (
                      <li key={`${ev.path}-${i}`} className="px-2.5 py-1.5 text-[11px] bg-white"
                        data-testid={`nas-ingest-detail-event-${i}`}>
                        <span className="block font-mono text-zinc-700 truncate">{ev.path}</span>
                        <span className="block text-[10px] text-zinc-500">
                          <span className={ev.status === 'failed' ? 'text-rose-700 font-semibold' : ''}>
                            {ev.status_label_ja || ev.status}
                          </span>
                          {' · '}{ev.reason_label || ev.reason}
                          {' · '}{fmtDT(ev.at)}
                        </span>
                      </li>
                    ))}
                    {rows.length === 0 && (
                      <li className="px-2.5 py-3 text-[11.5px] text-zinc-400 text-center">
                        該当する明細はありません / No matching events
                      </li>
                    )}
                  </ul>
                  {events?.has_more && !atCap && (
                    <Button size="sm" variant="outline" onClick={loadMore} disabled={more}
                      className="mt-1.5 h-7 text-[11px]" data-testid="nas-ingest-detail-events-more">
                      {more ? <CircleNotch size={12} className="mr-1 animate-spin" /> : null}
                      さらに {EVENT_PAGE} 件 / Load {EVENT_PAGE} more
                    </Button>
                  )}
                  {atCap && (
                    <p className="mt-1.5 text-[10.5px] text-amber-700" data-testid="nas-ingest-detail-events-capped">
                      画面表示は {MAX_EVENT_ROWS} 件までです（絞り込みを使ってください）。
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default NasIngestDetailSheet;
